/**
 * The last client log lines, kept in memory for "Report a problem" (docs/UI.md "Report a problem").
 *
 * `installClientLog()` wraps console.error / warn / info / log once (the originals still run) and records uncaught
 * errors and unhandled rejections as lines too. Nothing leaves the page from here: the report form sends the lines
 * only when the player presses SEND (they are told so). Engine-free and DOM-optional (headless tests).
 */
import { REPORT_LIMITS } from '../../shared/feedback';

export const LOG_LINES = 50;

export interface ClientLog {
  /** Oldest first, each clipped to REPORT_LIMITS.logLine. */
  lines(): string[];
  push(level: string, text: string): void;
}

function fmt(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v instanceof Error) return `${v.name}: ${v.message}`;
  try {
    return JSON.stringify(v) ?? String(v);
  } catch {
    return String(v);
  }
}

export function createClientLog(capacity = LOG_LINES, now: () => number = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())): ClientLog {
  const buf: string[] = [];
  const t0 = now();
  return {
    lines: () => buf.slice(),
    push(level, text) {
      const s = `${((now() - t0) / 1000).toFixed(1)}s ${level} ${text}`.replace(/\s+/g, ' ');
      buf.push(s.length > REPORT_LIMITS.logLine ? s.slice(0, REPORT_LIMITS.logLine) : s);
      if (buf.length > capacity) buf.splice(0, buf.length - capacity);
    },
  };
}

let shared: ClientLog | null = null;

/** The page-wide log (installed once; later calls return the same one). */
export function installClientLog(): ClientLog {
  if (shared) return shared;
  const log = createClientLog();
  shared = log;
  if (typeof console !== 'undefined') {
    for (const level of ['error', 'warn', 'info', 'log'] as const) {
      const orig = console[level].bind(console);
      console[level] = (...args: unknown[]) => {
        try {
          log.push(level, args.map(fmt).join(' '));
        } catch {
          /* never let logging break the caller */
        }
        orig(...args);
      };
    }
  }
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('error', (e) => log.push('uncaught', `${e.message} (${e.filename ?? '?'}:${e.lineno ?? 0})`));
    window.addEventListener('unhandledrejection', (e) => log.push('rejection', fmt((e as PromiseRejectionEvent).reason)));
  }
  return log;
}
